import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { readInstantCache, writeInstantCache } from '../utils/instantCache';
import { shouldRenderDesktop } from '../utils/deviceDetection';
import HandlerChat from './HandlerChat';
import { toast } from 'sonner';
import {
  Phone,
  WhatsappLogo,
  DotsThreeVertical,
  Gear,
  SignOut,
  ClipboardText,
  UsersFour,
  X,
  PaperPlaneTilt,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import MBSetupBadge from '../components/MBSetupBadge';
import CustomerDetailsPanel from '../components/CustomerDetailsPanel';
import formatChatTime from '../utils/formatChatTime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Point 48 — Calling Handler's stripped-down home screen: a Client
 * Directory (phone, last withdraw, last deposit) with one-tap call/
 * WhatsApp actions. No PT/G/PW/N badges, no chat panels by default. The
 * three-dot menu is locked to Settings + Logout — every other capability
 * (phone-display toggle, activity reports) lives behind Settings.
 *
 * A secondary "My Chats" tab (only shown if admin assigned MBs — the
 * default is no chats at all) gives read-only access to live incoming
 * chats via the existing /handler-chat/:roomId route, which renders in a
 * view-only mode for this role (see HandlerChat.js's isReadOnlyViewer).
 */
const CallingHandlerDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState('directory'); // 'directory' | 'reports'
  const [selectedChatId, setSelectedChatId] = useState(null); // #6 desktop split

  // Clear the middle chat + 3rd details panel whenever the handler switches tabs.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('dws:close-customer-details'));
    setSelectedChatId(null);
  }, [tab]);

  const isDesktop = shouldRenderDesktop();
  const [customers, setCustomers] = useState(() => readInstantCache('ch_directory', user?.id) || []);
  const [showFullPhone, setShowFullPhone] = useState(false);
  const [loading, setLoading] = useState(() => !readInstantCache('ch_directory', user?.id));
  const [reports, setReports] = useState(() => readInstantCache('ch_reports', user?.id) || []);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [openReport, setOpenReport] = useState(null); // activity entry being edited
  const [rNote, setRNote] = useState('');
  const [rDuration, setRDuration] = useState('');
  const [rSaving, setRSaving] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [search, setSearch] = useState('');

  const loadDirectory = async () => {
    if (!user?.id) return;
    if (!readInstantCache('ch_directory', user?.id)) setLoading(true);
    try {
      const res = await axios.get(`${API}/calling-handler/${user.id}/client-directory`);
      const _cust = res.data?.customers || [];
      setCustomers(_cust);
      writeInstantCache('ch_directory', user?.id, _cust);
      setShowFullPhone(!!res.data?.show_full_phone);
    } catch (e) {
      toast.error('Failed to load client directory');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDirectory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const loadReports = async () => {
    if (!user?.id) return;
    if (!readInstantCache('ch_reports', user?.id)) setReportsLoading(true);
    try {
      const [unsent, sent] = await Promise.all([
        axios.get(`${API}/calling-handler/${user.id}/activities`, { params: { sent: false } }),
        axios.get(`${API}/calling-handler/${user.id}/activities`, { params: { sent: true } }),
      ]);
      const acts = [...(unsent.data?.activities || []), ...(sent.data?.activities || [])]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setReports(acts);
      writeInstantCache('ch_reports', user?.id, acts);
    } catch (e) {
      toast.error('Failed to load reports');
    } finally {
      setReportsLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 'reports') loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, user?.id]);

  const handleLogout = () => {
    logout();
  };

  const logActivity = (customerId, kind) => {
    if (!user?.id) return;
    axios.post(`${API}/calling-handler/activity`, {
      handler_id: user.id,
      customer_id: customerId,
      kind,
    }).catch(() => {});
  };

  const handleCall = (row) => {
    const num = row.phone_full || row.phone;
    if (!num) { toast.error('No phone number'); return; }
    logActivity(row.customer_id, 'call');
    window.location.href = `tel:${num}`;
  };

  const handleWhatsApp = (row) => {
    logActivity(row.customer_id, 'whatsapp');
    const digits = String(row.phone_full || row.phone || '').replace(/[^0-9]/g, '');
    if (!digits) { toast.error('No phone number'); return; }
    const withCountry = digits.length === 10 ? `91${digits}` : digits;
    window.open(`https://wa.me/${withCountry}`, '_blank');
  };

  // Directory row click — desktop: open the customer's chat (view-only) in the
  // middle split AND their Customer Details in the 3rd split. Mobile: full profile.
  const handleDirectoryClick = (row) => {
    if (isDesktop) {
      setSelectedChatId(row.room_id || null);
      window.dispatchEvent(new CustomEvent('dws:open-customer-details', {
        detail: { customerId: row.customer_id, roomId: row.room_id || null, actorId: user?.id },
      }));
    } else {
      navigate(`/handler-profile/${row.customer_id}`);
    }
  };

  const openReportModal = (a) => {
    setOpenReport(a);
    setRNote(a.note || '');
    setRDuration(a.duration || '');
  };
  const saveReport = async () => {
    if (!openReport) return;
    setRSaving(true);
    try {
      await axios.put(`${API}/calling-handler/activity/${openReport.id}/note`, {
        handler_id: user.id, note: rNote, duration: rDuration,
      });
      setReports((prev) => prev.map((x) => x.id === openReport.id ? { ...x, note: rNote, duration: rDuration } : x));
      toast.success('Report saved');
      setOpenReport(null);
    } catch (e) { toast.error('Failed to save'); }
    finally { setRSaving(false); }
  };
  const sendReportToAdmin = async () => {
    if (!openReport) return;
    setRSaving(true);
    try {
      if (rNote !== (openReport.note || '') || rDuration !== (openReport.duration || '')) {
        await axios.put(`${API}/calling-handler/activity/${openReport.id}/note`, { handler_id: user.id, note: rNote, duration: rDuration });
      }
      await axios.post(`${API}/calling-handler/send-report`, {
        handler_id: user.id, activity_ids: [openReport.id], range_label: 'Manual',
      });
      toast.success('Report sent to admin');
      setOpenReport(null);
      loadReports();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to send'); }
    finally { setRSaving(false); }
  };

  const fmtAmt = (n) => (n === null || n === undefined ? '—' : `₹${Number(n).toLocaleString('en-IN')}`);

  const filteredCustomers = customers.filter((c) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (c.name || '').toLowerCase().includes(q)
      || (c.phone || '').includes(search.trim())
      || (c.serial_number || '').toLowerCase().includes(q)
      || (c.client_id || '').toLowerCase().includes(q);
  });

  return (
    <div className="h-screen flex bg-gray-50" data-testid="calling-handler-dashboard">
      <div className={`flex flex-col min-h-0 ${isDesktop ? 'w-[400px] flex-shrink-0 border-r border-gray-200 h-full' : 'flex-1 h-screen'}`}>
      {/* Header */}
      <div className="bg-[#008069] text-white px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <img src="/icons/icon-128x128.png" alt="DWS" className="w-8 h-8 rounded-full flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="font-bold truncate text-base">DWS Support</h1>
              <p className="text-[10px] text-white/70 truncate">Calling Handler</p>
            </div>
          </div>
          <div className="relative flex-shrink-0">
            <button
              onClick={() => setShowMenu((v) => !v)}
              className="p-2 hover:bg-white/10 rounded-full"
              data-testid="calling-dashboard-menu-btn"
            >
              <DotsThreeVertical size={22} weight="bold" />
            </button>
            {showMenu && (
              <>
                <div className="fixed inset-0 z-[90]" onClick={() => setShowMenu(false)} />
                <div className="absolute right-0 top-10 bg-white rounded-lg shadow-2xl border z-[100] w-52 py-1 text-gray-800">
                  <button
                    onClick={() => { setShowMenu(false); navigate('/calling-handler-settings'); }}
                    className="w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                    data-testid="calling-dashboard-settings-btn"
                  >
                    <Gear size={18} /> Settings
                  </button>
                  <div className="border-t my-1" />
                  <button
                    onClick={() => { setShowMenu(false); handleLogout(); }}
                    className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    data-testid="calling-dashboard-logout-btn"
                  >
                    <SignOut size={18} /> Logout
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {tab === 'directory' && (
          <div className="mt-2.5">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, phone, client code..."
              className="w-full bg-white/15 placeholder-white/60 text-white rounded-full px-4 py-2 text-sm focus:outline-none focus:bg-white/25"
              data-testid="calling-directory-search"
            />
          </div>
        )}
      </div>

      {/* Section tabs — Client Directory is the default/main screen; My
          Chats only matters once admin has assigned MBs (point 48: "no
          chat panels visible by default"). */}
      <div className="bg-white border-b flex flex-shrink-0">
        <button
          onClick={() => setTab('directory')}
          className={`flex-1 py-2.5 text-sm font-semibold flex items-center justify-center gap-1.5 ${tab === 'directory' ? 'text-[#008069] border-b-2 border-[#008069]' : 'text-gray-500'}`}
          data-testid="calling-tab-directory"
        >
          <UsersFour size={16} /> Client Directory
        </button>
        <button
          onClick={() => setTab('reports')}
          className={`flex-1 py-2.5 text-sm font-semibold flex items-center justify-center gap-1.5 ${tab === 'reports' ? 'text-[#008069] border-b-2 border-[#008069]' : 'text-gray-500'}`}
          data-testid="calling-tab-reports"
        >
          <ClipboardText size={16} /> Reports
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        {tab === 'directory' ? (
          loading ? (
            <div className="text-center py-10 text-gray-400 text-sm">Loading directory…</div>
          ) : filteredCustomers.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm">No clients found</div>
          ) : (
            <div className="divide-y divide-gray-100 bg-white">
              {filteredCustomers.map((row) => (
                <div key={row.customer_id} onClick={() => handleDirectoryClick(row)} className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 ${selectedChatId && selectedChatId === row.room_id ? 'bg-green-50' : ''}`} data-testid={`calling-directory-row-${row.customer_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-semibold text-gray-800 truncate">{row.name}</span>
                      {row.mb_setup && <MBSetupBadge value={row.mb_setup} size="xs" />}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">{row.phone || '—'}</div>
                    <div className="flex gap-4 mt-1 text-[11px]">
                      <span className="text-blue-700">
                        Last Withdraw: <b>{fmtAmt(row.last_withdraw_amount)}</b>
                        {row.last_withdraw_at && <span className="text-gray-400"> · {formatChatTime(row.last_withdraw_at)}</span>}
                      </span>
                      <span className="text-emerald-700">
                        Last Deposit: <b>{fmtAmt(row.last_deposit_amount)}</b>
                        {row.last_deposit_at && <span className="text-gray-400"> · {formatChatTime(row.last_deposit_at)}</span>}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleWhatsApp(row); }}
                      className="w-9 h-9 rounded-full bg-green-500 hover:bg-green-600 text-white flex items-center justify-center"
                      title="WhatsApp"
                      data-testid={`calling-directory-whatsapp-btn-${row.customer_id}`}
                    >
                      <WhatsappLogo size={18} weight="fill" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleCall(row); }}
                      className="w-9 h-9 rounded-full bg-[#008069] hover:bg-[#006e5a] text-white flex items-center justify-center"
                      title="Call"
                      data-testid={`calling-directory-call-btn-${row.customer_id}`}
                    >
                      <Phone size={18} weight="fill" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          reportsLoading ? (
            <div className="text-center py-10 text-gray-400 text-sm">Loading reports…</div>
          ) : reports.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm px-6">
              No activity yet — call or WhatsApp a client from the directory to create a report.
            </div>
          ) : (
            <div className="divide-y divide-gray-100 bg-white">
              {reports.map((a) => {
                const isSent = !!a.report_id;
                return (
                  <div
                    key={a.id}
                    onClick={() => openReportModal(a)}
                    className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50"
                    data-testid={`calling-report-row-${a.id}`}
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${a.kind === 'whatsapp' ? 'bg-green-100 text-green-600' : 'bg-teal-100 text-[#008069]'}`}>
                      {a.kind === 'whatsapp' ? <WhatsappLogo size={18} weight="fill" /> : <Phone size={18} weight="fill" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-gray-800 truncate">{a.client_snapshot?.name || 'Client'}</span>
                        {a.client_snapshot?.mb_setup && <MBSetupBadge value={a.client_snapshot.mb_setup} size="xs" />}
                        <span className="text-[10px] uppercase font-bold text-gray-400">{a.kind}</span>
                      </div>
                      <p className="text-xs text-gray-500 truncate mt-0.5">{a.note ? a.note : 'Tap to add report details'}</p>
                    </div>
                    <div className="flex flex-col items-end flex-shrink-0">
                      <span className="text-[11px] text-gray-400">{formatChatTime(a.created_at)}</span>
                      <span className={`text-[10px] font-bold mt-0.5 ${isSent ? 'text-emerald-600' : 'text-amber-600'}`}>{isSent ? 'SENT' : 'DRAFT'}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>
      </div>{/* End left column */}

      {/* #6 — desktop split: embedded (view-only) chat on the right */}
      {isDesktop && (
        selectedChatId ? (
          <div className="flex-1 min-h-0 relative overflow-hidden flex flex-col">
            <HandlerChat
              key={selectedChatId}
              embeddedRoomId={selectedChatId}
              onBackToList={() => setSelectedChatId(null)}
            />
          </div>
        ) : (
          <div className="flex-1 min-h-0 flex flex-col items-center justify-center bg-[#f0f2f5] border-l border-gray-200" data-testid="calling-idle-panel">
            <img src="/icons/customer/icon-192x192.png" alt="DWS Vault Chat" className="w-24 h-24 opacity-80 mb-4" />
            <h3 className="text-xl font-bold text-gray-700">DWS Vault Chat</h3>
            <p className="text-sm text-gray-500 mt-1">Kisi chat ko kholne ke liye left se select karo</p>
          </div>
        )
      )}

      {/* Report editor modal — add description + duration, save or send to admin */}
      {openReport && (
        <div className="fixed inset-0 z-[120] bg-black/50 flex items-center justify-center p-4" onClick={() => setOpenReport(null)} data-testid="calling-report-modal">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-[#008069] text-white px-4 py-3 flex items-center justify-between">
              <span className="font-bold flex items-center gap-2"><ClipboardText size={18} weight="bold" /> Call Report</span>
              <button onClick={() => setOpenReport(null)} className="p-1 hover:bg-white/10 rounded-full" data-testid="calling-report-close"><X size={18} weight="bold" /></button>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${openReport.kind === 'whatsapp' ? 'bg-green-100 text-green-600' : 'bg-teal-100 text-[#008069]'}`}>
                  {openReport.kind === 'whatsapp' ? <WhatsappLogo size={20} weight="fill" /> : <Phone size={20} weight="fill" />}
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-gray-800 truncate">{openReport.client_snapshot?.name || 'Client'}</div>
                  <div className="text-xs text-gray-500">{openReport.kind === 'whatsapp' ? 'WhatsApp redirect' : 'Call'} · {formatChatTime(openReport.created_at)}</div>
                </div>
              </div>
              {openReport.report_id ? (
                <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">This report was sent to admin (read-only).</div>
              ) : null}
              <div>
                <label className="text-xs font-semibold text-gray-500">Duration (kitni der baat hui)</label>
                <input
                  type="text"
                  value={rDuration}
                  onChange={(e) => setRDuration(e.target.value)}
                  disabled={!!openReport.report_id}
                  placeholder="e.g. 5 min"
                  className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#008069] disabled:bg-gray-50"
                  data-testid="calling-report-duration"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500">Description (kya baat hui)</label>
                <textarea
                  value={rNote}
                  onChange={(e) => setRNote(e.target.value)}
                  disabled={!!openReport.report_id}
                  rows={4}
                  placeholder="Kya baat hui, kya result raha..."
                  className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#008069] disabled:bg-gray-50 resize-none"
                  data-testid="calling-report-note"
                />
              </div>
              {!openReport.report_id && (
                <div className="flex gap-2 pt-1">
                  <button onClick={saveReport} disabled={rSaving} className="flex-1 py-2.5 rounded-lg border border-gray-300 text-gray-700 font-semibold text-sm hover:bg-gray-50 disabled:opacity-50" data-testid="calling-report-save">Save Draft</button>
                  <button onClick={sendReportToAdmin} disabled={rSaving} className="flex-1 py-2.5 rounded-lg bg-[#008069] hover:bg-[#006e5a] text-white font-semibold text-sm flex items-center justify-center gap-1.5 disabled:opacity-50" data-testid="calling-report-send"><PaperPlaneTilt size={16} weight="fill" /> Send to Admin</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 3rd split — Customer Details panel (opens on directory row tap, desktop) */}
      <CustomerDetailsPanel />
    </div>
  );
};

export default CallingHandlerDashboard;
